import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';

function OrganizationSettings() {
  const { orgId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDomains, setNewDomains] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [isNameUpdating, setIsNameUpdating] = useState(false);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoading(true);
        const orgDocRef = doc(db, 'organizations', orgId);
        const orgDoc = await getDoc(orgDocRef);
        if (orgDoc.exists()) {
          const orgData = { id: orgDoc.id, ...orgDoc.data() };
          setOrganization(orgData);
          setOrgName(orgData.name);
        } else {
          setError('Organization not found.');
        }
      } catch (err) {
        setError('Failed to fetch organization details.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganization();
  }, [orgId]);

  const handleUpdateName = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) {
      alert('Organization name cannot be empty.');
      return;
    }
    setIsNameUpdating(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('You must be logged in.');
      const idToken = await user.getIdToken();

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateOrganization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ orgId, name: orgName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update organization name.');
      }
      alert('Organization name updated successfully!');
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setIsNameUpdating(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    const domainsToAdd = newDomains.split(/[\s,]+/).filter(Boolean); // Split by space or comma and remove empty strings

    if (domainsToAdd.length === 0) {
      alert('Please enter at least one domain.');
      return;
    }

    setIsUpdating(true);
    try {
      const orgDocRef = doc(db, 'organizations', orgId);
      await updateDoc(orgDocRef, {
        allowedDomains: arrayUnion(...domainsToAdd)
      });

      // Refresh local state to show the new domains
      setOrganization(prev => ({
        ...prev,
        allowedDomains: [...new Set([...(prev.allowedDomains || []), ...domainsToAdd])]
      }));
      setNewDomains('');

    } catch (err) {
      alert('Failed to add domains. Please try again.');
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!organization) return null;

  return (
    <div className="container">
      <header className="page-header">
        <h1>Manage: {organization.name}</h1>
        <Link to="/admin" className="btn btn-secondary">← Back to Dashboard</Link>
      </header>

      <div className="profile-body">
        <main className="card-list" style={{ gridColumn: '1 / -1' }}>
          <div className="section">
            <h3>Edit Organization</h3>
            <form onSubmit={handleUpdateName}>
              <div className="form-group">
                <label htmlFor="orgName">Organization Name</label>
                <input
                  type="text"
                  id="orgName"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  disabled={isNameUpdating}
                />
              </div>
              <button type="submit" className="btn btn-primary" disabled={isNameUpdating}>
                {isNameUpdating ? 'Updating...' : 'Update Name'}
              </button>
            </form>
          </div>

          <div className="section">
            <h3>Allowed Email Domains</h3>
            <p>Users can only be invited if their email address belongs to one of these domains.</p>
            
            <div className="domain-list">
              {(organization.allowedDomains && organization.allowedDomains.length > 0) ? (
                <ul>
                  {organization.allowedDomains.map(domain => <li key={domain}>{domain}</li>)}
                </ul>
              ) : (
                <p>No domains configured.</p>
              )}
            </div>

            <form onSubmit={handleAddDomain} className="form-group">
              <label htmlFor="newDomains">New Domain(s)</label>
              <textarea
                id="newDomains"
                value={newDomains}
                onChange={(e) => setNewDomains(e.target.value)}
                placeholder="e.g., acme-corp.com, acme.dev"
                disabled={isUpdating}
                rows="3"
              />
              <button type="submit" className="btn btn-primary" disabled={isUpdating} style={{marginTop: '10px'}}>
                {isUpdating ? 'Adding...' : 'Add Domains'}
              </button>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}

export default OrganizationSettings;
